'use strict';

const libPictView = require('pict-view');
const libStickerSets = require('../StickerSets.js');

/**
 * StickerSetsPreview - the iteration surface for the curated sticker sets (#/sticker-sets-preview).
 * Renders every built-in set in StickerSets.js as a labeled grid of thumbnails, each served recolored
 * through the Shapes endpoint. A broken tile means a name that does not resolve, so editing
 * source/StickerSets.js and reloading is a tight loop for getting each set to feel right. Not in the
 * nav; reachable by URL. No data of its own, so any signed-in user can open it.
 */
class PictViewStickerSetsPreview extends libPictView
{
	show() { this.render(); }

	onBeforeRender(pRenderable)
	{
		let tmpShapes = this.pict.providers.ShapesAPI;
		let tmpSets = libStickerSets.SET_ORDER
			.filter((pKey) => !!libStickerSets.Sets[pKey])
			.map((pKey) =>
			{
				let tmpSet = libStickerSets.Sets[pKey];
				return {
					Key: pKey,
					Label: tmpSet.Label,
					Description: tmpSet.Description || '',
					Count: tmpSet.Items.length,
					Items: tmpSet.Items.map((pItem) => (
						{
							Url: tmpShapes ? tmpShapes.shapeURL(pItem.Collection, pItem.Name) : ('/1.0/Shapes/' + pItem.Collection + '/' + pItem.Name),
							Title: pItem.Title || pItem.Name,
							Ref: pItem.Collection + ':' + pItem.Name
						}))
				};
			});
		this.pict.AppData.StickerSetsPreview = { Sets: tmpSets, TotalSets: tmpSets.length };
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewStickerSetsPreview;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-StickerSetsPreview',
	DefaultRenderable: 'StickerSetsPreview',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.ssp { padding: 24px; max-width: 1120px; margin: 0 auto; box-sizing: border-box; }
		.ssp-intro { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 18px; }
		.ssp-set { margin: 0 0 26px; }
		.ssp-set-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
		.ssp-set-label { font-weight: 600; font-size: 15px; color: var(--theme-color-text-primary, #1d2230); }
		.ssp-set-meta { font-size: 12px; color: var(--theme-color-text-secondary, #9aa3b2); }
		.ssp-set-desc { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); margin: 2px 0 10px; }
		.ssp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(94px, 1fr)); gap: 10px; }
		.ssp-tile { display: flex; flex-direction: column; align-items: center; gap: 5px; padding: 10px 6px; border: 1px solid var(--theme-color-border-subtle, #eef1f6); border-radius: 8px; background: var(--theme-color-background-panel, #fff); }
		.ssp-img { width: 40px; height: 40px; object-fit: contain; }
		.ssp-title { font-size: 12px; color: var(--theme-color-text-primary, #1d2230); text-align: center; line-height: 1.15; }
		.ssp-ref { font-size: 10px; color: var(--theme-color-text-secondary, #9aa3b2); text-align: center; word-break: break-all; }
	`,
	Templates:
	[
		{
			Hash: 'StickerSetsPreview-Container',
			Template: /*html*/`
<div class="ssp">
	<h2 class="ps-page-h">Sticker sets</h2>
	<p class="ssp-intro">{~D:AppData.StickerSetsPreview.TotalSets~} built-in sets. Edit source/StickerSets.js and reload to iterate. A broken image is a name that does not exist.</p>
	{~TS:StickerSetsPreview-Set:AppData.StickerSetsPreview.Sets~}
</div>`
		},
		{
			Hash: 'StickerSetsPreview-Set',
			Template: /*html*/`
<div class="ssp-set">
	<div class="ssp-set-head"><span class="ssp-set-label">{~D:Record.Label~}</span><span class="ssp-set-meta">{~D:Record.Count~} stickers &middot; {~D:Record.Key~}</span></div>
	<div class="ssp-set-desc">{~D:Record.Description~}</div>
	<div class="ssp-grid">{~TS:StickerSetsPreview-Item:Record.Items~}</div>
</div>`
		},
		{
			Hash: 'StickerSetsPreview-Item',
			Template: /*html*/`<div class="ssp-tile"><img class="ssp-img" src="{~D:Record.Url~}" alt="" loading="lazy"><span class="ssp-title">{~D:Record.Title~}</span><span class="ssp-ref">{~D:Record.Ref~}</span></div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'StickerSetsPreview',
			TemplateHash: 'StickerSetsPreview-Container',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
